import { cn } from "@/shared/lib/utils.js";

/**
 * Avatar — chip squircle con iniciales. El color de fondo se deriva de forma
 * determinística del nombre (hash → paleta), así una misma entidad siempre
 * conserva su color. Paleta de tonos apagados acorde al papel FORJA.
 *
 * shared/ui NO depende de features — átomo autocontenido.
 */
interface AvatarProps {
  name: string;
  className?: string;
}

interface Tone {
  bg: string;
  fg: string;
}

/** Tonos apagados (bg + fg legible) que combinan con el papel cálido FORJA. */
const PALETTE: readonly [Tone, ...Tone[]] = [
  { bg: "#e7dccb", fg: "#5a4a2e" }, // arena
  { bg: "#d9e3dc", fg: "#33524a" }, // salvia
  { bg: "#dfe1ea", fg: "#3d4458" }, // pizarra
  { bg: "#ecd9d2", fg: "#7a3f30" }, // terracota
  { bg: "#dde6ea", fg: "#345661" }, // acero
  { bg: "#e6dee9", fg: "#534061" }, // ciruela
  { bg: "#e8e3cf", fg: "#5d5524" }, // oliva
  { bg: "#d6e2e6", fg: "#2f5560" }, // teal apagado
];

function initials(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const first = parts[0];
  if (!first) return "?";
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? first;
  return ((first[0] ?? "") + (last[0] ?? "")).toUpperCase();
}

function hashName(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) {
    h = (h << 5) - h + name.charCodeAt(i);
    h |= 0; // a 32-bit
  }
  return Math.abs(h);
}

export function Avatar({ name, className }: AvatarProps) {
  const color = PALETTE[hashName(name) % PALETTE.length] ?? PALETTE[0];
  return (
    <span
      aria-hidden
      className={cn(
        "inline-flex size-8 shrink-0 select-none items-center justify-center rounded-md font-mono text-[0.6875rem] font-semibold uppercase tracking-tight",
        className,
      )}
      style={{ backgroundColor: color.bg, color: color.fg }}
    >
      {initials(name)}
    </span>
  );
}
